import z from "zod";
// Relative, not the `@/` alias, on purpose: the root Next app type-checks this
// file too (src/lib/schemas/funnelv2.ts imports TrackingFormValues from it
// across the app boundary), and there `@/` maps to the root `src/`, not to
// apps/crm-app/src — so an aliased import here fails the Vercel build.
import { pixelIdProblem, PIXEL_ID_PROBLEM_MESSAGES } from "../../../../../lib/tracking/pixel-id";

/**
 * Single pixel/container ID field (TAG-1871). `.min(1)` alone let a merchant
 * paste an entire TikTok <script> snippet in here; `superRefine` (rather than a
 * transform) keeps the inferred type a plain string so the form values are
 * unchanged.
 */
/**
 * Same hard rule for every id field, with the field's own name in the message:
 * Clarity asks for a project ID, so telling that merchant their "Pixel ID" is
 * required would point at a field that is not on screen.
 */
const trackingIdField = (label: string) =>
    z.string().superRefine((value, ctx) => {
        const problem = pixelIdProblem(value);
        if (!problem) return;
        const message = PIXEL_ID_PROBLEM_MESSAGES[problem].replace(/pixel id/gi, label);
        ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    });

const pixelIdField = trackingIdField('Pixel ID');

export enum TrackingType {
    FACEBOOK = 'facebook',
    TIKTOK = 'tiktok',
    SNAPCHAT = 'snapchat',
    PINTEREST = 'pinterest',
    GTM = "gtm",
    CLARITY = 'clarity'
}

export type TrackingTypePixel = TrackingType.FACEBOOK | TrackingType.TIKTOK | TrackingType.SNAPCHAT | TrackingType.PINTEREST;

const trackingSchemaBase = z.object({
    enabled: z.boolean(),
});

export const gtmSchema = trackingSchemaBase.extend({
    containerId: pixelIdField,
    googleAdsConversionId: z.string().optional(),
    googleAdsConversionLabel: z.string().optional(),
    events: z.object({
        PageView: z.boolean(),
        InitiateCheckout: z.boolean(),
        Purchase: z.boolean(),
        AddToCart: z.boolean(),
        ViewContent: z.boolean(),
    }),
});

/**
 * Microsoft Clarity. Its identifier is a project ID — that is the field the
 * store integration stores — and it has no ads-conversion settings.
 */
export const claritySchema = trackingSchemaBase.extend({
    projectId: trackingIdField('Project ID'),
    events: z.object({
        PageView: z.boolean(),
        InitiateCheckout: z.boolean(),
        Purchase: z.boolean(),
        AddToCart: z.boolean(),
        ViewContent: z.boolean(),
    }),
});

export const metaConversionSchema = trackingSchemaBase.extend({
    accessToken: z.string().min(1, 'Conversion ID is required'),
    pixelId: z.string().min(1, 'Pixel ID is required'),
    publishPurchaseIfNewCustomerOnly: z.boolean(),
});

const pixelBaseEvents = z.object({
    PageView: z.boolean(),
    InitiateCheckout: z.boolean(),
    Purchase: z.boolean(),
    AddToCart: z.boolean().optional(),
    ViewContent: z.boolean().optional(),
});

export const pixelBase = trackingSchemaBase.extend({
    pixelId: pixelIdField,
    events: pixelBaseEvents,
});

export const pixelSnapchatSchema = pixelBase.extend({
    events: z.object({
        ...pixelBaseEvents.shape,
        Search: z.boolean().optional(),
        AddToWishlist: z.boolean().optional(),
        CompleteRegistration: z.boolean().optional(),
    }),
});

// Extract event keys from each schema using Zod's type inference
type BaseEventsType = z.infer<typeof pixelBaseEvents>;
export type SnapchatEventsType = z.infer<typeof pixelSnapchatSchema>['events'];

// Map-based types to keep things scalable per tracking type
type TrackingSchemaMap = {
    [TrackingType.FACEBOOK]: typeof pixelBase;
    [TrackingType.TIKTOK]: typeof pixelBase;
    [TrackingType.SNAPCHAT]: typeof pixelSnapchatSchema;
    [TrackingType.PINTEREST]: typeof pixelBase;
    [TrackingType.GTM]: typeof gtmSchema;
    [TrackingType.CLARITY]: typeof claritySchema;
};

// Get the correct form values for a tracking type
type FormValuesForTrackingType<T extends TrackingType> = z.infer<TrackingSchemaMap[T]>;

type EventsSchemaMap = {
    [TrackingType.FACEBOOK]: BaseEventsType;
    [TrackingType.TIKTOK]: BaseEventsType;
    [TrackingType.SNAPCHAT]: SnapchatEventsType;
    [TrackingType.PINTEREST]: BaseEventsType;
    [TrackingType.GTM]: z.infer<typeof gtmSchema>['events'];
    [TrackingType.CLARITY]: z.infer<typeof claritySchema>['events'];
};

// Get the correct event keys for a tracking type
type EventKeysForTrackingType<T extends TrackingType> = keyof EventsSchemaMap[T];

export type PixelFormValues = FormValuesForTrackingType<TrackingTypePixel>;
export type GTMFormValues = FormValuesForTrackingType<TrackingType.GTM>;
export type ClarityFormValues = FormValuesForTrackingType<TrackingType.CLARITY>;
export type TrackingFormValues = FormValuesForTrackingType<TrackingType>;

export type TrackingInfosExtended = {
    [TrackingType.FACEBOOK]: PixelTrackingInfos;
    [TrackingType.TIKTOK]: PixelTrackingInfos;
    [TrackingType.SNAPCHAT]: PixelTrackingInfos;
    [TrackingType.PINTEREST]: PixelTrackingInfos;
    [TrackingType.GTM]: GTMTrackingInfos;
    [TrackingType.CLARITY]: ClarityTrackingInfos;
}

export type TrackingInfos<T extends TrackingType> = {
    name: string;
    icon: string;
    alt: string;
    events?: Array<{ key: EventKeysForTrackingType<T>; label: string }>;
    info?: string;
    setupSteps?: string[];
    hasSpecialSettings?: boolean;
} & TrackingInfosExtended[T];

export type MetaConversionTrackingInfos = {
    accessTokenPlaceholder: string;
    accessTokenDescription: string;
    accessTokenRegex?: RegExp;
    pixelIdPlaceholder: string;
    pixelIdDescription: string;
    pixelIdRegex?: RegExp;
};

export type PixelTrackingInfos = {
    pixelIdPlaceholder: string;
    pixelIdDescription: string;
    pixelIdRegex?: RegExp;
};

export type GTMTrackingInfos = {
    containerIdPlaceholder: string;
    containerIdDescription: string;
    containerIdRegex?: RegExp;
};

export type ClarityTrackingInfos = {
    projectIdPlaceholder: string;
    projectIdDescription: string;
    projectIdRegex?: RegExp;
};